import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { commissionService } from '@/services/commissionService'

export function useMyCommissions(recipientId: string, status?: string) {
  return useQuery({
    queryKey: ['my-commissions', recipientId, status],
    queryFn: () => commissionService.getMyCommissions(recipientId, status),
    enabled: !!recipientId,
  })
}

export function useMyBalance(recipientId: string) {
  return useQuery({
    queryKey: ['my-balance', recipientId],
    queryFn: () => commissionService.getMyBalance(recipientId),
    enabled: !!recipientId,
  })
}

export function useCommissionsByRecipient(recipientId: string, status?: string) {
  return useQuery({
    queryKey: ['commissions', 'recipient', recipientId, status],
    queryFn: () => commissionService.getCommissionsByRecipient(recipientId, status),
    enabled: !!recipientId,
  })
}

export function useCommissionsByEnrollment(enrollmentId: string) {
  return useQuery({
    queryKey: ['commissions', 'enrollment', enrollmentId],
    queryFn: () => commissionService.getCommissionsByEnrollment(enrollmentId),
    enabled: !!enrollmentId,
  })
}

export function useRecipientBalance(recipientId: string) {
  return useQuery({
    queryKey: ['balance', 'recipient', recipientId],
    queryFn: () => commissionService.getBalance(recipientId),
    enabled: !!recipientId,
  })
}

export function useCommissionSummary(recipientId: string) {
  return useQuery({
    queryKey: ['commission-summary', recipientId],
    queryFn: () => commissionService.getCommissionSummary(recipientId),
    enabled: !!recipientId,
  })
}

export function useProcessPendingCommissions() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => commissionService.processPendingCommissions(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['commissions'] })
      queryClient.invalidateQueries({ queryKey: ['my-commissions'] })
      queryClient.invalidateQueries({ queryKey: ['my-balance'] })
      queryClient.invalidateQueries({ queryKey: ['balance'] })
      queryClient.invalidateQueries({ queryKey: ['commission-summary'] })
    },
  })
}

export function useCancelCommission() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (enrollmentId: string) => commissionService.cancelCommission(enrollmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['commissions'] })
      queryClient.invalidateQueries({ queryKey: ['my-commissions'] })
      queryClient.invalidateQueries({ queryKey: ['my-balance'] })
      queryClient.invalidateQueries({ queryKey: ['balance'] })
    },
  })
}
